import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DropdownField from '../../DropDownField/DropDownField';
import RequiredField from '../../../SharedComponents/RequiredField/RequiredField';
import { COUNTRIES } from '../../../../Constants/countries';
import {
    IDENTIFICATION_REQUIRED_FIELDS,
    IDENTIFICATION_DEFAULT_VALUES,
    IDENTIFICATION_CONDITIONAL_RULES
} from './identificationConfig';

const IdentificationFormFields = ({
                                      formData = {},
                                      handleChange,
                                      errors = {},
                                      readOnly = false
                                  }) => {
    const { t } = useTranslation('identificationDoc');

    // NO MORE DUPLICATION! Use centralized configuration
    const requiredFields = IDENTIFICATION_REQUIRED_FIELDS;
    const defaultValues = IDENTIFICATION_DEFAULT_VALUES;

    // Merge defaults with provided formData, ensuring null values are converted to empty strings
    const safeFormData = Object.keys(defaultValues).reduce((acc, key) => {
        acc[key] = formData?.[key] ?? defaultValues[key];
        return acc;
    }, { ...defaultValues });

    // Helper function to safely get form values
    const getFieldValue = (fieldName) => {
        return safeFormData[fieldName];
    };

    // Helper to check if a field is required
    const isFieldRequired = (fieldName) => {
        // Check if field is explicitly required
        if (requiredFields[fieldName] === true) {
            return true;
        }

        // Check if field is conditionally required (expiry date when ID is provided)
        for (const [idField, expiryField] of Object.entries(IDENTIFICATION_CONDITIONAL_RULES)) {
            if (fieldName === expiryField) {
                const idValue = safeFormData[idField];
                if (idValue && idValue.toString().trim() !== '') {
                    return true; // Expiry date is required when ID is provided
                }
            }
        }

        return false;
    };

    // Helper to check if field is empty
    const isFieldEmpty = (fieldName) => {
        const value = safeFormData[fieldName];
        return !value || value.toString().trim() === '';
    };

    // Helper to determine if a field should show error styling
    const shouldShowError = (fieldName) => {
        // Show error if there's a validation error from server/validation
        if (errors[fieldName]) {
            return true;
        }

        // Show red border for conditionally required fields (real-time feedback)
        if (isFieldRequired(fieldName) && isFieldEmpty(fieldName)) {
            return true;
        }

        return false;
    };

    // Get field error message
    const getErrorMessage = (fieldName) => {
        // Return server validation error if it exists
        if (errors[fieldName]) {
            return errors[fieldName];
        }

        // Generate real-time error message for conditionally required fields
        if (isFieldRequired(fieldName) && isFieldEmpty(fieldName)) {
            // Check if this is a conditional requirement
            for (const [idField, expiryField] of Object.entries(IDENTIFICATION_CONDITIONAL_RULES)) {
                if (fieldName === expiryField) {
                    const idValue = safeFormData[idField];
                    if (idValue && idValue.toString().trim() !== '') {
                        // Use translation with dynamic field names
                        const expiryFieldLabel = t(expiryField);
                        const idFieldLabel = t(idField);
                        return t('expiry_date_required_when_id_provided', {
                            expiryField: expiryFieldLabel,
                            idField: idFieldLabel
                        });
                    }
                }
            }
            // Regular required field message
            const fieldLabel = t(fieldName);
            return t('field_is_required', { field: fieldLabel });
        }

        return '';
    };

    // Custom styles for required field borders
    const getFieldStyle = (fieldName) => {
        if (readOnly) return {};

        if (shouldShowError(fieldName)) {
            return {
                borderColor: '#dc3545',
                borderWidth: '2px',
                boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
            };
        }
        return {};
    };

    // Helper to get label with asterisk for required fields
    const getFieldLabel = (fieldName, translationKey) => {
        const baseLabel = t(translationKey);
        return isFieldRequired(fieldName) ? `${baseLabel} *` : baseLabel;
    };

    return (
        <>
            {/* National Passport */}
            <Row className="mb-3">
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('national_passport', 'national_passport')}
                        name="national_passport"
                        value={getFieldValue('national_passport')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('national_passport')}
                        errorText={getErrorMessage('national_passport')}
                        style={getFieldStyle('national_passport')}
                        readOnly={readOnly}
                    />
                </Col>
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('national_passport_expiry_date', 'national_passport_expiry_date')}
                        name="national_passport_expiry_date"
                        type="date"
                        value={getFieldValue('national_passport_expiry_date')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('national_passport_expiry_date')}
                        errorText={getErrorMessage('national_passport_expiry_date')}
                        style={getFieldStyle('national_passport_expiry_date')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>

            {/* Driver's License */}
            <Row className="mb-3">
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('drivers_license', 'drivers_license')}
                        name="drivers_license"
                        value={getFieldValue('drivers_license')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('drivers_license')}
                        errorText={getErrorMessage('drivers_license')}
                        style={getFieldStyle('drivers_license')}
                        readOnly={readOnly}
                    />
                </Col>
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('drivers_license_expiry_date', 'drivers_license_expiry_date')}
                        name="drivers_license_expiry_date"
                        type="date"
                        value={getFieldValue('drivers_license_expiry_date')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('drivers_license_expiry_date')}
                        errorText={getErrorMessage('drivers_license_expiry_date')}
                        style={getFieldStyle('drivers_license_expiry_date')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>

            {/* Diplomatic Card */}
            <Row className="mb-3">
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('diplomatic_card', 'diplomatic_card')}
                        name="diplomatic_card"
                        value={getFieldValue('diplomatic_card')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('diplomatic_card')}
                        errorText={getErrorMessage('diplomatic_card')}
                        style={getFieldStyle('diplomatic_card')}
                        readOnly={readOnly}
                    />
                </Col>
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('diplomatic_card_expiry_date', 'diplomatic_card_expiry_date')}
                        name="diplomatic_card_expiry_date"
                        type="date"
                        value={getFieldValue('diplomatic_card_expiry_date')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('diplomatic_card_expiry_date')}
                        errorText={getErrorMessage('diplomatic_card_expiry_date')}
                        style={getFieldStyle('diplomatic_card_expiry_date')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>

            {/* National ID Card */}
            <Row className="mb-3">
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('national_id_card', 'national_id_card')}
                        name="national_id_card"
                        value={getFieldValue('national_id_card')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('national_id_card')}
                        errorText={getErrorMessage('national_id_card')}
                        style={getFieldStyle('national_id_card')}
                        readOnly={readOnly}
                    />
                </Col>
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('national_id_card_expiry_date', 'national_id_card_expiry_date')}
                        name="national_id_card_expiry_date"
                        type="date"
                        value={getFieldValue('national_id_card_expiry_date')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('national_id_card_expiry_date')}
                        errorText={getErrorMessage('national_id_card_expiry_date')}
                        style={getFieldStyle('national_id_card_expiry_date')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>

            {/* UNLP */}
            <Row className="mb-3">
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('unlp', 'unlp')}
                        name="unlp"
                        value={getFieldValue('unlp')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('unlp')}
                        errorText={getErrorMessage('unlp')}
                        style={getFieldStyle('unlp')}
                        readOnly={readOnly}
                    />
                </Col>
                <Col md={6}>
                    <RequiredField
                        label={getFieldLabel('unlp_expiry_date', 'unlp_expiry_date')}
                        name="unlp_expiry_date"
                        type="date"
                        value={getFieldValue('unlp_expiry_date')}
                        onChange={handleChange}
                        isInvalid={shouldShowError('unlp_expiry_date')}
                        errorText={getErrorMessage('unlp_expiry_date')}
                        style={getFieldStyle('unlp_expiry_date')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>

            {/* Additional validation message for identification documents */}
            {!readOnly && (
                <Row className="mb-3">
                    <Col>
                        <Form.Text className="text-muted">
                            <small>
                                <em>{t('identification_note')}</em>
                            </small>
                        </Form.Text>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default IdentificationFormFields;